import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import type { MRT_Column, MRT_ColumnFiltersState, MRT_RowData, MRT_TableInstance } from "material-react-table";
import { useState } from "react";
import { columnLabel, isDataColumn } from "_shared/_views/tables/table-card-layout";
import { dayOf } from "_shared/_views/tables/table-date-range-filter";
import { SheetHeading, sheetPaperSx } from "_shared/_views/tables/table-sheet";

/**
 * Every column the table can filter by, hidden or shown: the desktop table's filter row offers a
 * hidden column's filter once the reader shows it, and a phone reader should not have to (#6715).
 */
export function filterableColumns<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): MRT_Column<TData>[] {
  return table.getAllLeafColumns().filter((column) => isDataColumn(column) && column.getCanFilter());
}

/** Whether a filter value narrows anything: an empty text, an empty pick or an open range does not. */
export function isFilterValueSet(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(isFilterValueSet);
  return value !== undefined && value !== null && value !== "";
}

/** How many of the filters the sheet offers are set, for the count on the Filter button. */
export function countSetFilters<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): number {
  const offered = new Set(filterableColumns(table).map((column) => column.id));
  return table.getState().columnFilters.filter((filter) => offered.has(filter.id) && isFilterValueSet(filter.value)).length;
}

type FilterOption = { label: string; value: string };

function optionsOf<TData extends MRT_RowData>(column: MRT_Column<TData>): FilterOption[] {
  return (column.columnDef.filterSelectOptions ?? []).map((option) =>
    typeof option === "string"
      ? { label: option, value: option }
      : { label: String(option.label ?? option.value), value: String(option.value) },
  );
}

/** A day as a date input holds it, "2027-03-31", read where the reader is rather than in UTC. */
export function dateInputValue(value: unknown): string {
  const day = dayOf(value);
  if (!day) return "";
  const month = String(day.getMonth() + 1).padStart(2, "0");
  const date = String(day.getDate()).padStart(2, "0");
  return `${day.getFullYear()}-${month}-${date}`;
}

/**
 * The local day a date input names. `new Date("2027-03-31")` would be midnight in UTC, which is the
 * evening before anywhere west of it.
 */
export function dayOfDateInput(value: string): Date | undefined {
  const [year, month, date] = value.split("-").map(Number);
  if (!year || !month || !date) return undefined;
  return new Date(year, month - 1, date);
}

/**
 * The filters the sheet applies: each set value in the draft, and every filter the sheet does not
 * offer kept as it was — a link's own criteria, such as a proposal status, are not the sheet's to drop.
 */
export function appliedFilters(
  current: MRT_ColumnFiltersState,
  offeredIds: readonly string[],
  draft: Readonly<Record<string, unknown>>,
): MRT_ColumnFiltersState {
  const kept = current.filter((filter) => !offeredIds.includes(filter.id));
  const set = offeredIds
    .filter((id) => isFilterValueSet(draft[id]))
    .map((id) => ({ id, value: draft[id] }));
  return [...kept, ...set];
}

const Fields = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  overflowY: "auto",
  padding: theme.spacing(1, 2, 2),
}));

const DateRange = styled("div")(({ theme }) => ({ display: "flex", gap: theme.spacing(1) }));

const Actions = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
}));

function FilterField<TData extends MRT_RowData>(
  props: Readonly<{ column: MRT_Column<TData>; value: unknown; onChange: (value: unknown) => void }>,
) {
  const { column, value, onChange } = props;
  const label = columnLabel(column);
  const variant = column.columnDef.filterVariant;

  if (variant === "select" || variant === "multi-select") {
    const multiple = variant === "multi-select";
    const selected = multiple ? (Array.isArray(value) ? value.map(String) : []) : String(value ?? "");
    return (
      <TextField
        select
        label={label}
        value={selected}
        onChange={(event) => onChange(event.target.value)}
        slotProps={{ select: { multiple } }}
        fullWidth
      >
        {!multiple && <MenuItem value="">Any</MenuItem>}
        {optionsOf(column).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (variant === "date-range") {
    const [first, last] = Array.isArray(value) ? value : [];
    const setDay = (index: 0 | 1, input: string) => {
      const range = [first, last];
      range[index] = dayOfDateInput(input);
      onChange(range);
    };
    return (
      <DateRange role="group" aria-label={label}>
        <TextField
          type="date"
          label={`${label} from`}
          value={dateInputValue(first)}
          onChange={(event) => setDay(0, event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
        <TextField
          type="date"
          label={`${label} to`}
          value={dateInputValue(last)}
          onChange={(event) => setDay(1, event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
      </DateRange>
    );
  }

  return (
    <TextField label={label} value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} fullWidth />
  );
}

function FilterSheetBody<TData extends MRT_RowData>(
  props: Readonly<{ table: MRT_TableInstance<TData>; onClose: () => void }>,
) {
  "use no memo";

  const { table, onClose } = props;
  const columns = filterableColumns(table);
  // A draft, applied in one go: a list filtered on the server would otherwise ask again at each key.
  const [draft, setDraft] = useState<Record<string, unknown>>(() =>
    Object.fromEntries(table.getState().columnFilters.map((filter) => [filter.id, filter.value])),
  );
  const offeredIds = columns.map((column) => column.id);

  function apply(next: Readonly<Record<string, unknown>>) {
    table.setColumnFilters((current) => appliedFilters(current, offeredIds, next));
    onClose();
  }

  return (
    <>
      <Fields>
        {columns.map((column) => (
          <FilterField
            key={column.id}
            column={column}
            value={draft[column.id]}
            onChange={(value) => setDraft((current) => ({ ...current, [column.id]: value }))}
          />
        ))}
      </Fields>
      <Actions>
        <Button onClick={() => apply({})}>Clear all</Button>
        <Button variant="contained" onClick={() => apply(draft)}>
          Show results
        </Button>
      </Actions>
    </>
  );
}

/**
 * The phone's Filter: a bottom sheet with a field for each column the table filters by (#6715). The
 * fields are drafts until Show results, which applies them together and closes the sheet; Clear all
 * clears them. A filter the sheet does not offer is left alone.
 */
export function TableFilterSheet<TData extends MRT_RowData>(
  props: Readonly<{ table: MRT_TableInstance<TData>; open: boolean; onClose: () => void }>,
) {
  const { table, open, onClose } = props;
  return (
    <Drawer anchor="bottom" open={open} onClose={onClose} slotProps={{ paper: { sx: sheetPaperSx, "aria-labelledby": "table-filter-sheet-title" } }}>
      <SheetHeading id="table-filter-sheet-title" title="Filter" onClose={onClose} />
      {/* Drawn only while open, so each opening starts from the filters the table has now. */}
      {open && <FilterSheetBody table={table} onClose={onClose} />}
    </Drawer>
  );
}
